'use client'

import { useState, useEffect, useRef } from 'react'
import { useTranslations } from 'next-intl'
import { DownloadIcon, FileIcon, ImageIcon, Trash2Icon, XIcon } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Progress } from '@/components/ui/progress'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { DeleteConfirmationDialog } from '@/components/partials'
import { formatFileSize, textLimit } from '@/lib/utils'
import { useFilesStore } from '@/store'

const DownloadsDropdown = () => {
  const t = useTranslations()
  const downloads = useFilesStore(state => state.downloads.items)
  const cancelDownload = useFilesStore(state => state.cancelDownload)
  const removeFromDownloadQueue = useFilesStore(state => state.removeFromDownloadQueue)
  const clearDownloads = useFilesStore(state => state.clearDownloads)
  const addToDownloadQueue = useFilesStore(state => state.addToDownloadQueue)
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false)
  const [selectedDownloadId, setSelectedDownloadId] = useState<string | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const prevDownloadsLengthRef = useRef(downloads.length)

  // Automatically open dropdown when a new item is added to the download queue
  useEffect(() => {
    // If the number of downloads has increased, open the dropdown
    if (downloads.length > prevDownloadsLengthRef.current) {
      setIsOpen(true)
    }
    // Update the ref with the current length
    prevDownloadsLengthRef.current = downloads.length
  }, [downloads.length])

  // Add beforeunload event listener to show confirmation dialog when closing tab or refreshing page
  useEffect(() => {
    const hasActiveDownloads = downloads.some(download => download.status === 'downloading')

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasActiveDownloads) {
        // Standard way to show a confirmation dialog when closing the tab or refreshing
        // Modern browsers standardized on preventDefault() and ignore custom messages
        // for security reasons, showing their own generic confirmation dialog instead
        event.preventDefault()
        // For legacy browser support, return a string (the returnValue assignment is no longer needed)
        return t('active_downloads_confirmation')
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [downloads, t])

  const handleCancelDownload = (downloadId: string) => {
    setSelectedDownloadId(downloadId)
    setShowCancelConfirmation(true)
  }

  const confirmCancelDownload = () => {
    if (selectedDownloadId) {
      cancelDownload(selectedDownloadId)
      setShowCancelConfirmation(false)
      setSelectedDownloadId(null)
      toast.success(t('download_canceled'))
    }
  }

  return (
    <>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="icon" className="relative">
            <DownloadIcon className="h-4 w-4" />
            {downloads.length > 0 && (
              <Badge className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center p-0 text-xs">
                {downloads.length}
              </Badge>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="border-sidebar-border w-80 max-w-[calc(100vw-1rem)]">
          <DropdownMenuLabel className="flex items-center justify-between">
            <span>{t('downloads')}</span>
            {downloads.some(d => d.status === 'completed' || d.status === 'canceled') && (
              <Button variant="outline" size="sm" onClick={clearDownloads} className="h-7 text-xs">
                <XIcon size="12" />
                {t('clear')}
              </Button>
            )}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {downloads.length === 0 ? (
            <div className="text-muted-foreground p-4 text-center text-sm">{t('no_downloads')}</div>
          ) : (
            <ScrollArea className="h-80">
              <div className="space-y-3 p-3">
                {downloads.map(download => (
                  <div key={download.downloadId} className="rounded-md border p-3">
                    <div className="mb-2 flex items-center justify-between">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex items-center gap-2">
                            {download.file && download.file.type && download.file.type.startsWith('image/') ? (
                              <ImageIcon className="h-4 w-4" />
                            ) : (
                              <FileIcon className="h-4 w-4" />
                            )}
                            <span className="text-sm">
                              {download.file && download.file.name ? textLimit(download.file.name, 10) : 'Unknown'}
                            </span>
                            <span className="text-muted-foreground text-xs">
                              {download.file && download.file.size ? formatFileSize(download.file.size) : ''}
                            </span>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{download.file && download.file.name ? download.file.name : 'Unknown file'}</p>
                        </TooltipContent>
                      </Tooltip>
                      <div className="flex items-center gap-2">
                        {download.status === 'downloading' && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => handleCancelDownload(download.downloadId)}>
                            <XIcon className="h-4 w-4" />
                          </Button>
                        )}
                        {(download.status === 'completed' || download.status === 'canceled') && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            title={t('download_again')}
                            onClick={() => {
                              // Use the file object directly if it exists
                              if (download.file) {
                                addToDownloadQueue(download.file)
                              }
                            }}>
                            <DownloadIcon className="h-4 w-4" />
                          </Button>
                        )}
                        {(download.status === 'completed' ||
                          download.status === 'error' ||
                          download.status === 'canceled') && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => removeFromDownloadQueue(download.downloadId)}>
                            <Trash2Icon className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center justify-between">
                        <div className="text-xs">
                          {download.status === 'pending' && t('pending')}
                          {download.status === 'downloading' && t('downloading')}
                          {download.status === 'completed' && t('completed')}
                          {download.status === 'error' && t('error')}
                          {download.status === 'canceled' && t('canceled')}
                        </div>
                        <div className="text-xs">{download.progress}%</div>
                      </div>
                      <Progress
                        value={download.progress}
                        className={`w-full ${download.status === 'canceled' ? 'bg-gray-300' : ''}`}
                      />
                      {download.error && <div className="text-destructive mt-1 text-xs">{download.error}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <DeleteConfirmationDialog
        open={showCancelConfirmation}
        onCancel={() => {
          setShowCancelConfirmation(false)
          setSelectedDownloadId(null)
        }}
        onConfirm={confirmCancelDownload}
        title={t('cancel_download')}
        description={t('cancel_download_confirmation')}
      />
    </>
  )
}

export default DownloadsDropdown
